import Icon from '../../../components/AppIcon';

const ServiceAreaMap = () => {
  const serviceAreas = [
    {
      region: "Johannesburg",
      coverage: "Full Coverage",
      responseTime: "30-45 minutes",
      neighborhoods: ["Sandton", "Rosebank", "Randburg", "Fourways", "Midrand"],
      icon: "MapPin"
    },
    {
      region: "Pretoria",
      coverage: "Full Coverage",
      responseTime: "30-45 minutes",
      neighborhoods: ["Centurion", "Hatfield", "Menlyn", "Brooklyn", "Waterkloof"],
      icon: "MapPin"
    },
    {
      region: "East Rand",
      coverage: "Extended Coverage",
      responseTime: "45-60 minutes",
      neighborhoods: ["Kempton Park", "Boksburg", "Benoni", "Edenvale", "Germiston"],
      icon: "MapPin"
    },
    {
      region: "West Rand",
      coverage: "Extended Coverage",
      responseTime: "45-60 minutes",
      neighborhoods: ["Roodepoort", "Krugersdorp", "Randfontein", "Westonaria"],
      icon: "MapPin"
    }
  ];

  const commitments = [
    {
      icon: "Clock",
      title: "24/7 Emergency Service",
      description: "Round-the-clock availability for urgent plumbing emergencies across all service areas."
    },
    {
      icon: "Zap",
      title: "Rapid Response",
      description: "Average response time under 60 minutes for emergency calls in primary coverage areas."
    },
    {
      icon: "Shield",
      title: "Local Expertise",
      description: "Deep knowledge of local plumbing systems, building codes, and common regional issues."
    },
    {
      icon: "Award",
      title: "Quality Guarantee",
      description: "Same high-quality service and workmanship across all areas we serve."
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Map" size={20} color="var(--color-primary)" />
            <span className="text-sm font-semibold text-primary">Service Coverage</span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Areas We Serve
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Providing professional plumbing services across Gauteng with rapid emergency response times.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 md:mb-16">
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border">
            <div className="aspect-[4/3] rounded-xl overflow-hidden mb-6">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Service Area Map - Gauteng Region"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=-26.2041,28.0473&z=10&output=embed"
                className="border-0"
              ></iframe>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="MapPin" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="font-headline text-xl font-semibold text-foreground">Primary Service Area</h3>
                <p className="text-sm text-muted-foreground">Gauteng Province, South Africa</p>
              </div>
            </div>
            <p className="text-muted-foreground">
              Our primary service area covers the greater Gauteng region, with fastest response times in Johannesburg and Pretoria. We also provide extended coverage to surrounding areas.
            </p>
          </div>

          <div className="space-y-4">
            {serviceAreas?.map((area, index) => (
              <div 
                key={index}
                className="bg-card rounded-xl p-6 border border-border hover:border-primary hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={area?.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h4 className="font-headline text-lg font-semibold text-foreground">
                        {area?.region}
                      </h4>
                      <p className="text-sm text-muted-foreground">{area?.coverage}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-accent">
                      <Icon name="Clock" size={14} />
                      <span className="text-xs font-semibold">{area?.responseTime}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {area?.neighborhoods?.map((neighborhood, nIndex) => (
                    <span 
                      key={nIndex}
                      className="px-3 py-1 bg-muted rounded-full text-xs text-foreground"
                    >
                      {neighborhood}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {commitments?.map((commitment, index) => (
            <div 
              key={index}
              className="bg-card rounded-xl p-6 border border-border text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name={commitment?.icon} size={28} color="var(--color-primary)" />
              </div>
              <h4 className="font-headline text-lg font-semibold text-foreground mb-2">
                {commitment?.title}
              </h4>
              <p className="text-sm text-muted-foreground">
                {commitment?.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-muted/50 to-muted/30 rounded-2xl p-8 md:p-12">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-4">
              Don't See Your Area Listed?
            </h3>
            <p className="font-body text-lg text-muted-foreground mb-6">
              We're constantly expanding our service coverage. Call us to check if we can serve your location or to discuss special arrangements for areas outside our standard coverage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:0827935785"
                className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-lg font-semibold transition-colors emergency-cta"
              >
                <Icon name="Phone" size={20} />
                <span>Call: 082 793 5785</span>
              </a>
              <button className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg font-semibold transition-colors">
                <Icon name="MessageCircle" size={20} />
                <span>Send Inquiry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;